import React, { useRef, useEffect, useState } from 'react';
import { MapPinIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useBranch } from '../../context/BranchContext';

interface BranchSelectorProps {
  className?: string;
}

const BranchSelector: React.FC<BranchSelectorProps> = ({ className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { selectedBranch, branches, isLoading, selectBranch } = useBranch();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleSelectBranch = (branch: any) => {
    selectBranch(branch);
    setIsOpen(false);
  };

  if (isLoading) {
    return (
      <div
        className={`flex items-center p-2 border border-gray-300 rounded-lg bg-white text-sm text-gray-700 ${className}`}
      >
        <div className='animate-pulse'>Loading...</div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className='flex items-center p-2 border border-gray-300 rounded-lg bg-white text-sm text-gray-700 hover:border-primary'
      >
        <MapPinIcon className='h-5 w-5 text-primary mr-2' />
        <div className='text-left'>
          <div className='font-medium'>Cửa hàng</div>
          <div className='text-xs text-gray-500 truncate max-w-[180px]'>
            {selectedBranch?.name || 'Chọn chi nhánh'}
          </div>
        </div>
        <ChevronDownIcon
          className={`h-4 w-4 ml-2 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className='absolute left-0 mt-2 w-80 bg-white rounded-lg shadow-lg z-[100] py-1 border border-gray-200'>
          <div className='p-3 border-b border-gray-100 bg-orange-50'>
            <div className='text-xs text-orange-700'>
              <p className='font-medium'>Giao hoặc đến lấy tại:</p>
              <p className='mt-1'>
                {selectedBranch?.name} - {selectedBranch?.address}
              </p>
            </div>
          </div>

          <div className='p-3'>
            <p className='text-xs text-gray-700 mb-3'>
              Chọn cửa hàng gần bạn nhất để tối ưu chi phí giao hàng. Hoặc đến
              lấy hàng
            </p>
            <div className='space-y-4'>
              {branches?.map((branch) => (
                <button
                  key={branch._id}
                  className={`w-full text-left px-3 py-2 rounded-md hover:bg-gray-50 flex items-start ${
                    selectedBranch?._id === branch._id ? 'bg-orange-50' : ''
                  }`}
                  onClick={() => handleSelectBranch(branch)}
                >
                  <MapPinIcon
                    className={`h-5 w-5 mr-2 flex-shrink-0 ${
                      selectedBranch?._id === branch._id
                        ? 'text-orange-500'
                        : 'text-gray-400'
                    }`}
                  />
                  <div>
                    <div
                      className={`text-sm font-medium ${
                        selectedBranch?._id === branch._id
                          ? 'text-orange-700'
                          : 'text-gray-800'
                      }`}
                    >
                      {branch.name}
                    </div>
                    <div className='text-xs text-gray-500 mt-0.5'>
                      {branch.address}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BranchSelector;
